/**
 * <WhoNotFor /> — "Who CARTIE is NOT for" anti-sales section.
 *
 * v34.67 (Jun 3 2026) — Added per Nova's brutal review (third pass):
 * "A tool that is for everyone is a tool that isn't optimized for anyone.
 *  Excluding bad-fit customers is the ultimate trust signal in B2B."
 *
 * Numbers calibrated against actual product economics:
 *   - Entry tier: $497/mo
 *   - Pledge: 3× ROI → must save ≥ $1,491/mo
 *   - Typical waste rate: 5–10% of cloud spend
 *   - Therefore minimum useful cloud bill: ~$30K/mo
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { XCircle, ArrowRight } from 'lucide-react';

const EXCLUSIONS = [
  {
    headline: 'Your cloud bill is under $30k/month',
    body: 'Our 3× ROI pledge means you need to save at least $1.5k/mo to break even on the $497 tier. Below $30k of total spend, the math doesn\'t work for either of us. Use AWS Cost Explorer + a budget alert. Come back when you scale.',
  },
  {
    headline: 'You don\'t use LLMs in production',
    body: '~40% of our 26 engines are for token observability, semantic caching, prompt compaction, and model arbitrage. If you\'re a pure infra company with zero LLM spend, you\'re paying for engines you\'ll never use. Vantage might serve you better.',
  },
  {
    headline: 'Your engineers can\'t touch GitHub or the CLI',
    body: 'Our One-Click Fix lives in your CI as a GitHub PR. Our diagnostics live in `npx @cartieai/cli why`. If your security policy blocks GitHub Apps or shell tools from external SaaS, you won\'t get value out of us. Stick with a read-only dashboard tool.',
  },
  {
    headline: 'You need SOC 2 Type II today',
    body: 'We\'re aligned with SOC 2 controls and a Type II audit is engaged with Vanta — but we won\'t have the badge until late 2026. If your procurement team requires a current Type II report before any signature, we\'re too early for you. Talk to us in Q4 — we\'ll save your slot.',
  },
];

const WhoNotFor = () => (
  <section
    className="relative py-16 px-4 bg-slate-950 border-y border-rose-500/10"
    data-testid="who-not-for-section"
  >
    {/* subtle rose glow to signal "honest exclusion" — anti-sales energy */}
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[300px] rounded-full bg-rose-500/[0.04] blur-[120px]" />
    </div>

    <div className="max-w-5xl mx-auto relative">
      <div className="text-center mb-10">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-rose-500/10 border border-rose-500/30 text-[11px] font-mono uppercase tracking-[0.18em] text-rose-300 mb-4">
          <XCircle className="w-3 h-3" /> Anti-sales · the honest list
        </div>
        <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-[-0.02em] leading-tight text-white mb-3">
          Who CARTIE is <span className="text-rose-400">not for</span>
        </h2>
        <p className="text-sm text-slate-400 max-w-2xl mx-auto leading-relaxed">
          Every B2B vendor says they're "for everyone." That's never true. Here's the
          honest list of teams we'll fail — so you don't waste a trial.
        </p>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        {EXCLUSIONS.map((item, i) => (
          <div
            key={i}
            className="group rounded-xl border border-white/10 bg-slate-900 p-5 transition-all hover:border-rose-400/40"
            data-testid={`who-not-for-card-${i}`}
          >
            <div className="flex items-start gap-3">
              <XCircle className="w-5 h-5 text-rose-400 mt-0.5 shrink-0" />
              <div>
                <h3 className="text-base font-bold text-white mb-2 leading-snug">
                  {item.headline}
                </h3>
                <p className="text-[13px] text-slate-400 leading-relaxed">{item.body}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-10 text-center">
        <p className="text-sm text-slate-400 mb-3">
          Still here? Then we're probably a great fit.
        </p>
        <Link
          to="/pricing"
          className="inline-flex items-center gap-2 text-emerald-300 hover:text-emerald-200 font-semibold text-sm transition-colors"
          data-testid="who-not-for-cta-pricing"
        >
          See pricing + the 3× ROI pledge
          <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  </section>
);

export default WhoNotFor;
